import React, { forwardRef, type SVGProps } from "react";

/** Vendored Lucide SVG paths with a `lucide-react`-compatible component API. */

export type IconProps = SVGProps<SVGSVGElement>;

type IconNode = ReadonlyArray<readonly [tag: string, attrs: Record<string, string | number>]>;

const BASE_PROPS: SVGProps<SVGSVGElement> = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  width: 24,
  height: 24,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

/** Build an icon with optional defaults; caller props take precedence. */
function createLucideIcon(iconName: string, iconNode: IconNode, defaultProps: SVGProps<SVGSVGElement> = {}) {
  const Icon = forwardRef<SVGSVGElement, IconProps>((props, ref) => (
    <svg ref={ref} {...BASE_PROPS} {...defaultProps} {...props}>
      {iconNode.map(([tag, attrs], i) => React.createElement(tag, { key: i, ...attrs }))}
    </svg>
  ));
  Icon.displayName = iconName;
  return Icon;
}

// — Sidebar navigation (order matches app-shell nav groups) —
export const Trophy = createLucideIcon("Trophy", [
  ["path", { d: "M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978" }],
  ["path", { d: "M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978" }],
  ["path", { d: "M18 9h1.5a1 1 0 0 0 0-5H18" }],
  ["path", { d: "M4 22h16" }],
  ["path", { d: "M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z" }],
  ["path", { d: "M6 9H4.5a1 1 0 0 1 0-5H6" }],
]);

export const Users = createLucideIcon("Users", [
  ["path", { d: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" }],
  ["path", { d: "M16 3.128a4 4 0 0 1 0 7.744" }],
  ["path", { d: "M22 21v-2a4 4 0 0 0-3-3.87" }],
  ["circle", { cx: 9, cy: 7, r: 4 }],
]);

export const Banknote = createLucideIcon("Banknote", [
  ["rect", { width: 20, height: 12, x: 2, y: 6, rx: 2 }],
  ["circle", { cx: 12, cy: 12, r: 2 }],
  ["path", { d: "M6 12h.01M18 12h.01" }],
]);

export const WalletCards = createLucideIcon("WalletCards", [
  ["rect", { width: 18, height: 18, x: 3, y: 3, rx: 2 }],
  ["path", { d: "M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2" }],
  ["path", { d: "M3 11h3c.8 0 1.6.3 2.1.9l1.1.9c1.6 1.6 4.1 1.6 5.7 0l1.1-.9c.5-.5 1.3-.9 2.1-.9H21" }],
]);

export const LayoutDashboard = createLucideIcon("LayoutDashboard", [
  ["rect", { width: 7, height: 9, x: 3, y: 3, rx: 1 }],
  ["rect", { width: 7, height: 5, x: 14, y: 3, rx: 1 }],
  ["rect", { width: 7, height: 9, x: 14, y: 12, rx: 1 }],
  ["rect", { width: 7, height: 5, x: 3, y: 16, rx: 1 }],
]);

export const Truck = createLucideIcon("Truck", [
  ["path", { d: "M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" }],
  ["path", { d: "M15 18H9" }],
  ["path", { d: "M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" }],
  ["circle", { cx: 17, cy: 18, r: 2 }],
  ["circle", { cx: 7, cy: 18, r: 2 }],
]);

export const MessagesSquare = createLucideIcon("MessagesSquare", [
  ["path", { d: "M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" }],
  ["path", { d: "M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1" }],
]);

export const GraduationCap = createLucideIcon("GraduationCap", [
  ["path", { d: "M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z" }],
  ["path", { d: "M22 10v6" }],
  ["path", { d: "M6 12.5V16a6 3 0 0 0 12 0v-3.5" }],
]);

export const ContactRound = createLucideIcon("ContactRound", [
  ["path", { d: "M16 2v2" }],
  ["path", { d: "M17.915 22a6 6 0 0 0-12 0" }],
  ["path", { d: "M8 2v2" }],
  ["circle", { cx: 12, cy: 12, r: 4 }],
]);

/** Figma sidebar — «Консультации с клин. экспертом» (filled 16×16 badge glyph). */
export const ExpertConsultation = createLucideIcon(
  "ExpertConsultation",
  [
    [
      "path",
      {
        d: "M10.1667 2.66659V1.33325C10.1667 1.05711 10.3905 0.833252 10.6667 0.833252C10.9428 0.833252 11.1667 1.05711 11.1667 1.33325V2.66659C11.1667 2.94273 10.9428 3.16659 10.6667 3.16659C10.3905 3.16659 10.1667 2.94273 10.1667 2.66659Z",
        fill: "currentColor",
      },
    ],
    [
      "path",
      {
        d: "M11.4433 14.6666C11.4433 13.7383 11.0743 12.8484 10.4179 12.192C9.76157 11.5356 8.87159 11.1666 7.94333 11.1666C7.01508 11.1666 6.1251 11.5356 5.46872 12.192C4.81235 12.8484 4.44333 13.7383 4.44333 14.6666C4.44333 14.9427 4.21948 15.1666 3.94333 15.1666C3.66719 15.1666 3.44333 14.9427 3.44333 14.6666C3.44333 13.4731 3.91713 12.3282 4.76104 11.4843C5.60496 10.6404 6.74986 10.1666 7.94333 10.1666C9.13681 10.1666 10.2817 10.6404 11.1256 11.4843C11.9695 12.3282 12.4433 13.4731 12.4433 14.6666C12.4433 14.9427 12.2195 15.1666 11.9433 15.1666C11.6672 15.1666 11.4433 14.9427 11.4433 14.6666Z",
        fill: "currentColor",
      },
    ],
    [
      "path",
      {
        d: "M4.83333 2.66659V1.33325C4.83333 1.05711 5.05719 0.833252 5.33333 0.833252C5.60948 0.833252 5.83333 1.05711 5.83333 1.33325V2.66659C5.83333 2.94273 5.60948 3.16659 5.33333 3.16659C5.05719 3.16659 4.83333 2.94273 4.83333 2.66659Z",
        fill: "currentColor",
      },
    ],
    [
      "path",
      {
        d: "M10.1667 7.99992C10.1667 6.8033 9.19662 5.83325 8 5.83325C6.80338 5.83325 5.83333 6.8033 5.83333 7.99992C5.83333 9.19654 6.80338 10.1666 8 10.1666C9.19662 10.1666 10.1667 9.19654 10.1667 7.99992ZM11.1667 7.99992C11.1667 9.74882 9.7489 11.1666 8 11.1666C6.2511 11.1666 4.83333 9.74882 4.83333 7.99992C4.83333 6.25102 6.2511 4.83325 8 4.83325C9.7489 4.83325 11.1667 6.25102 11.1667 7.99992Z",
        fill: "currentColor",
      },
    ],
    [
      "path",
      {
        d: "M13.5 3.99992C13.5 3.53968 13.1269 3.16659 12.6667 3.16659H3.33333C2.8731 3.16659 2.5 3.53968 2.5 3.99992V13.3333C2.5 13.7935 2.8731 14.1666 3.33333 14.1666H12.6667C13.1269 14.1666 13.5 13.7935 13.5 13.3333V3.99992ZM14.5 13.3333C14.5 14.3458 13.6792 15.1666 12.6667 15.1666H3.33333C2.32081 15.1666 1.5 14.3458 1.5 13.3333V3.99992C1.5 2.9874 2.32081 2.16659 3.33333 2.16659H12.6667C13.6792 2.16659 14.5 2.9874 14.5 3.99992V13.3333Z",
        fill: "currentColor",
      },
    ],
  ],
  { viewBox: "0 0 16 16", fill: "currentColor", stroke: "none" },
);

export const Flag = createLucideIcon("Flag", [
  ["path", { d: "M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.333 2q2 0 3.067-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8A6 6 0 0 1 16 16c-3 0-5-2-8-2a6 6 0 0 0-4 1.528" }],
]);

export const FileText = createLucideIcon("FileText", [
  ["path", { d: "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" }],
  ["path", { d: "M14 2v5a1 1 0 0 0 1 1h5" }],
  ["path", { d: "M10 9H8" }],
  ["path", { d: "M16 13H8" }],
  ["path", { d: "M16 17H8" }],
]);

export const Files = createLucideIcon("Files", [
  ["path", { d: "M15 2h-4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8" }],
  ["path", { d: "M16.706 2.706A2.4 2.4 0 0 0 15 2v5a1 1 0 0 0 1 1h5a2.4 2.4 0 0 0-.706-1.706z" }],
  ["path", { d: "M5 7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 1.732-1" }],
]);

export const Grid2x2 = createLucideIcon("Grid2x2", [
  ["path", { d: "M12 3v18" }],
  ["path", { d: "M3 12h18" }],
  ["rect", { x: 3, y: 3, width: 18, height: 18, rx: 2 }],
]);

export const Shapes = createLucideIcon("Shapes", [
  ["path", { d: "M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z" }],
  ["rect", { x: 3, y: 14, width: 7, height: 7, rx: 1 }],
  ["circle", { cx: 17.5, cy: 17.5, r: 3.5 }],
]);

// — Topbar / chrome —
export const Ellipsis = createLucideIcon("Ellipsis", [
  ["circle", { cx: 12, cy: 12, r: 1 }],
  ["circle", { cx: 19, cy: 12, r: 1 }],
  ["circle", { cx: 5, cy: 12, r: 1 }],
]);

export const CircleQuestionMark = createLucideIcon("CircleQuestionMark", [
  ["circle", { cx: 12, cy: 12, r: 10 }],
  ["path", { d: "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" }],
  ["path", { d: "M12 17h.01" }],
]);

export const Search = createLucideIcon("Search", [
  ["path", { d: "m21 21-4.34-4.34" }],
  ["circle", { cx: 11, cy: 11, r: 8 }],
]);

// Lucide `x` — used by the shared SearchInput's clear button.
export const X = createLucideIcon("X", [
  ["path", { d: "M18 6 6 18" }],
  ["path", { d: "m6 6 12 12" }],
]);

// The change-password checklist uses a bare tick rather than a circle-wrapped icon.
export const Check = createLucideIcon("Check", [["path", { d: "M20 6 9 17l-5-5" }]]);

export const Link = createLucideIcon("Link", [
  ["path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }],
  ["path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" }],
]);

export const ChevronDown = createLucideIcon("ChevronDown", [["path", { d: "m6 9 6 6 6-6" }]]);
export const ChevronUp = createLucideIcon("ChevronUp", [["path", { d: "m18 15-6-6-6 6" }]]);

// Mobile "scroll to top" button.
export const ArrowUp = createLucideIcon("ArrowUp", [
  ["path", { d: "m5 12 7-7 7 7" }],
  ["path", { d: "M12 19V5" }],
]);

// Matches the upload glyph used by patient-form.css; ArrowUp has no tray.
export const Upload = createLucideIcon("Upload", [
  ["path", { d: "M12 16V4m0 0 4 4m-4-4-4 4" }],
  ["path", { d: "M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" }],
]);

export const ChevronLeft = createLucideIcon("ChevronLeft", [["path", { d: "m15 18-6-6 6-6" }]]);

// — Profile / forms (added for the profile / delivery redesign batch) —
export const SquarePen = createLucideIcon("SquarePen", [
  ["path", { d: "M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" }],
  ["path", { d: "M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" }],
]);

export const SquarePlus = createLucideIcon("SquarePlus", [
  ["rect", { width: 18, height: 18, x: 3, y: 3, rx: 2 }],
  ["path", { d: "M8 12h8" }],
  ["path", { d: "M12 8v8" }],
]);

export const LogOut = createLucideIcon("LogOut", [
  ["path", { d: "m16 17 5-5-5-5" }],
  ["path", { d: "M21 12H9" }],
  ["path", { d: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" }],
]);

export const Calendar = createLucideIcon("Calendar", [
  ["path", { d: "M8 2v4" }],
  ["path", { d: "M16 2v4" }],
  ["rect", { width: 18, height: 18, x: 3, y: 4, rx: 2 }],
  ["path", { d: "M3 10h18" }],
]);

export const ChevronsUpDown = createLucideIcon("ChevronsUpDown", [
  ["path", { d: "m7 15 5 5 5-5" }],
  ["path", { d: "m7 9 5-5 5 5" }],
]);

// Rating star — rendered FILLED (`fill: currentColor`) so `tw-text-*` colors the whole glyph, matching
// the solid stars in the Figma rating (replaces `@heroicons/react/24/solid` StarIcon).
export const Star = createLucideIcon(
  "Star",
  [
    [
      "path",
      {
        d: "M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z",
      },
    ],
  ],
  { fill: "currentColor", stroke: "none" },
);

// — Patients table: status badges + actions —
// The status badge conveys state through the ICON colour (text stays muted): orange Clock = waiting,
// muted Loader = in-progress, green CircleCheck = finished, muted CircleX = canceled. Verified against
// the cached `patients-pc.png` status column.
export const Clock = createLucideIcon("Clock", [
  ["circle", { cx: "12", cy: "12", r: "10" }],
  ["path", { d: "M12 6v6l4 2" }],
]);
// lucide `clock-fading` — the muted "В работе" (in-production) status glyph (Figma «Badge status_web»
// state="В работе" → "Icon / clock-fading", node 7106:13674). A clock face inside a fading ring.
export const ClockFading = createLucideIcon("ClockFading", [
  ["path", { d: "M12 2a10 10 0 0 1 7.38 16.75" }],
  ["path", { d: "M12 6v6l4 2" }],
  ["path", { d: "M2.5 8.875a10 10 0 0 0-.5 3" }],
  ["path", { d: "M2.83 16a10 10 0 0 0 2.43 3.4" }],
  ["path", { d: "M4.636 5.235a10 10 0 0 1 .891-.857" }],
  ["path", { d: "M8.644 21.42a10 10 0 0 0 7.631-.38" }],
]);
export const Loader = createLucideIcon("Loader", [
  ["path", { d: "M12 2v4" }],
  ["path", { d: "m16.2 7.8 2.9-2.9" }],
  ["path", { d: "M18 12h4" }],
  ["path", { d: "m16.2 16.2 2.9 2.9" }],
  ["path", { d: "M12 18v4" }],
  ["path", { d: "m4.9 19.1 2.9-2.9" }],
  ["path", { d: "M2 12h4" }],
  ["path", { d: "m4.9 4.9 2.9 2.9" }],
]);
export const CircleCheck = createLucideIcon("CircleCheck", [
  ["circle", { cx: "12", cy: "12", r: "10" }],
  ["path", { d: "m9 12 2 2 4-4" }],
]);
export const CircleX = createLucideIcon("CircleX", [
  ["circle", { cx: "12", cy: "12", r: "10" }],
  ["path", { d: "m15 9-6 6" }],
  ["path", { d: "m9 9 6 6" }],
]);
// SquarePlus / SquarePen / ChevronLeft are declared once above (profile/forms batch) — reused here.
export const ChevronRight = createLucideIcon("ChevronRight", [["path", { d: "m9 18 6-6-6-6" }]]);
// Double chevrons for the patient-table pagination first/last (Figma 7342-56082 «start»/«end» icon buttons).
export const ChevronsLeft = createLucideIcon("ChevronsLeft", [
  ["path", { d: "m11 17-5-5 5-5" }],
  ["path", { d: "m18 17-5-5 5-5" }],
]);
export const ChevronsRight = createLucideIcon("ChevronsRight", [
  ["path", { d: "m6 17 5-5-5-5" }],
  ["path", { d: "m13 17 5-5-5-5" }],
]);
export const Menu = createLucideIcon("Menu", [
  ["path", { d: "M4 5h16" }],
  ["path", { d: "M4 12h16" }],
  ["path", { d: "M4 19h16" }],
]);
// square-menu — the v1.3 mobile bottom-bar «Меню» glyph (Figma Icon/square-menu 7332:18451): hamburger
// bars inside a rounded square (distinct from the plain `Menu` bars used elsewhere).
export const SquareMenu = createLucideIcon("SquareMenu", [
  ["rect", { width: "18", height: "18", x: "3", y: "3", rx: "2" }],
  ["path", { d: "M7 8h10" }],
  ["path", { d: "M7 12h10" }],
  ["path", { d: "M7 16h10" }],
]);
// Lucide file-type glyphs for chat attachments (frames 5291-126507 / 5289-123835).
// (FileText already exists above — reused for the "doc" kind.)
export const FileCode = createLucideIcon("FileCode", [
  ["path", { d: "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" }],
  ["path", { d: "M14 2v5a1 1 0 0 0 1 1h5" }],
  ["path", { d: "M10 12.5 8 15l2 2.5" }],
  ["path", { d: "m14 12.5 2 2.5-2 2.5" }],
]);
export const FileArchive = createLucideIcon("FileArchive", [
  ["path", { d: "M13.659 22H18a2 2 0 0 0 2-2V8a2.4 2.4 0 0 0-.706-1.706l-3.588-3.588A2.4 2.4 0 0 0 14 2H6a2 2 0 0 0-2 2v11.5" }],
  ["path", { d: "M14 2v5a1 1 0 0 0 1 1h5" }],
  ["path", { d: "M8 12v-1" }],
  ["path", { d: "M8 18v-2" }],
  ["path", { d: "M8 7V6" }],
  ["circle", { cx: "8", cy: "20", r: "2" }],
]);
export const FileCog = createLucideIcon("FileCog", [
  ["path", { d: "M15 8a1 1 0 0 1-1-1V2a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8z" }],
  ["path", { d: "M20 8v12a2 2 0 0 1-2 2h-4.182" }],
  ["path", { d: "m3.305 19.53.923-.382" }],
  ["path", { d: "M4 10.592V4a2 2 0 0 1 2-2h8" }],
  ["path", { d: "m4.228 16.852-.924-.383" }],
  ["path", { d: "m5.852 15.228-.383-.923" }],
  ["path", { d: "m5.852 20.772-.383.924" }],
  ["path", { d: "m8.148 15.228.383-.923" }],
  ["path", { d: "m8.53 21.696-.382-.924" }],
  ["path", { d: "m9.773 16.852.922-.383" }],
  ["path", { d: "m9.773 19.148.922.383" }],
  ["circle", { cx: "7", cy: "18", r: "3" }],
]);
export const FileSpreadsheet = createLucideIcon("FileSpreadsheet", [
  ["path", { d: "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" }],
  ["path", { d: "M14 2v5a1 1 0 0 0 1 1h5" }],
  ["path", { d: "M8 13h2" }],
  ["path", { d: "M14 13h2" }],
  ["path", { d: "M8 17h2" }],
  ["path", { d: "M14 17h2" }],
]);
export const FilePlay = createLucideIcon("FilePlay", [
  ["path", { d: "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" }],
  ["path", { d: "M14 2v5a1 1 0 0 0 1 1h5" }],
  ["path", { d: "M15.033 13.44a.647.647 0 0 1 0 1.12l-4.065 2.352a.645.645 0 0 1-.968-.56v-4.704a.645.645 0 0 1 .967-.56z" }],
]);
export const CircleAlert = createLucideIcon("CircleAlert", [
  ["circle", { cx: "12", cy: "12", r: "10" }],
  ["line", { x1: "12", x2: "12", y1: "8", y2: "12" }],
  ["line", { x1: "12", x2: "12.01", y1: "16", y2: "16" }],
]);
// The patients "waiting" status uses a scalloped badge outline, not a plain circle.
export const BadgeAlert = createLucideIcon("BadgeAlert", [
  ["path", { d: "M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" }],
  ["line", { x1: "12", x2: "12", y1: "8", y2: "12" }],
  ["line", { x1: "12", x2: "12.01", y1: "16", y2: "16" }],
]);

export const RotateCw = createLucideIcon("RotateCw", [
  ["path", { d: "M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1.06 6.74 2.74L21 8" }],
  ["path", { d: "M21 3v5h-5" }],
]);

export const FlipHorizontal = createLucideIcon("FlipHorizontal", [
  ["path", { d: "M8 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h3" }],
  ["path", { d: "M16 3h3a2 2 0 0 1 2 2v14c0 1.1-.9 2-2 2h-3" }],
  ["path", { d: "M12 20v2" }],
  ["path", { d: "M12 14v2" }],
  ["path", { d: "M12 8v2" }],
  ["path", { d: "M12 2v2" }],
]);

export const FlipVertical = createLucideIcon("FlipVertical", [
  ["path", { d: "M21 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3" }],
  ["path", { d: "M21 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3" }],
  ["path", { d: "M4 12H2" }],
  ["path", { d: "M10 12H8" }],
  ["path", { d: "M16 12h-2" }],
  ["path", { d: "M22 12h-2" }],
]);

export const Sparkles = createLucideIcon("Sparkles", [
  [
    "path",
    {
      d: "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z",
    },
  ],
  ["path", { d: "M20 3v4" }],
  ["path", { d: "M22 5h-4" }],
  ["path", { d: "M4 17v2" }],
  ["path", { d: "M5 18H3" }],
]);

// CT-link stepper icons for the add-patient recipe form.
export const Plus = createLucideIcon("Plus", [
  ["path", { d: "M5 12h14" }],
  ["path", { d: "M12 5v14" }],
]);
export const Minus = createLucideIcon("Minus", [["path", { d: "M5 12h14" }]]);
export const SlidersHorizontal = createLucideIcon("SlidersHorizontal", [
  ["path", { d: "M10 5H3" }],
  ["path", { d: "M12 19H3" }],
  ["path", { d: "M14 3v4" }],
  ["path", { d: "M16 17v4" }],
  ["path", { d: "M21 12h-9" }],
  ["path", { d: "M21 19h-5" }],
  ["path", { d: "M21 5h-7" }],
  ["path", { d: "M8 10v4" }],
  ["path", { d: "M8 12H3" }],
]);
// Lucide `funnel` — the v1.2 education «Фильтры» toggle button glyph (frame 7258-28718).
export const Funnel = createLucideIcon("Funnel", [
  ["path", { d: "M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" }],
]);
export const OctagonAlert = createLucideIcon("OctagonAlert", [
  ["path", { d: "M12 16h.01" }],
  ["path", { d: "M12 8v4" }],
  [
    "path",
    {
      d: "M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z",
    },
  ],
]);

// — Auth (login / recovery) field icons (Figma 7431:23957 user / 7431:23961 lock / 7431:23959 eye-off) —
export const User = createLucideIcon("User", [
  ["path", { d: "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" }],
  ["circle", { cx: "12", cy: "7", r: "4" }],
]);
export const Lock = createLucideIcon("Lock", [
  ["rect", { width: "18", height: "11", x: "3", y: "11", rx: "2", ry: "2" }],
  ["path", { d: "M7 11V7a5 5 0 0 1 10 0v4" }],
]);
export const Eye = createLucideIcon("Eye", [
  ["path", { d: "M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" }],
  ["circle", { cx: "12", cy: "12", r: "3" }],
]);
export const EyeOff = createLucideIcon("EyeOff", [
  ["path", { d: "M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" }],
  ["path", { d: "M14.084 14.158a3 3 0 0 1-4.242-4.242" }],
  ["path", { d: "M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" }],
  ["path", { d: "m2 2 20 20" }],
]);

// — Expert-consultation meeting card mini-actions (Figma 7262-29984: «Добавить в календарь» /
// «Перенести встречу» / «Отменить встречу») —
export const CalendarCheck = createLucideIcon("CalendarCheck", [
  ["path", { d: "M8 2v3" }],
  ["path", { d: "M16 2v3" }],
  ["rect", { x: "3", y: "3", width: "18", height: "18", rx: "2" }],
  ["path", { d: "M3 9h18" }],
  ["path", { d: "m9 15 2 2 4-4" }],
]);
export const CornerUpRight = createLucideIcon("CornerUpRight", [
  ["path", { d: "m15 14 5-5-5-5" }],
  ["path", { d: "M4 20v-7a4 4 0 0 1 4-4h12" }],
]);

// — Chat widget (Figma 5294-129412: attachment/checked-file/send glyphs) —
export const FileCheck = createLucideIcon("FileCheck", [
  ["path", { d: "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" }],
  ["path", { d: "M14 2v5a1 1 0 0 0 1 1h5" }],
  ["path", { d: "m9 15 2 2 4-4" }],
]);
export const Paperclip = createLucideIcon("Paperclip", [["path", { d: "m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18.03 8.9l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" }]]);
export const Send = createLucideIcon("Send", [
  ["path", { d: "M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" }],
  ["path", { d: "m21.854 2.147-10.94 10.939" }],
]);
export const Maximize2 = createLucideIcon("Maximize2", [
  ["path", { d: "M15 3h6v6" }],
  ["path", { d: "m21 3-7 7" }],
  ["path", { d: "m3 21 7-7" }],
  ["path", { d: "M9 21H3v-6" }],
]);

// — Smart-prescription audio widget (redesign-v1 port of the voice-guided recipe feature) —
export const Mic = createLucideIcon("Mic", [
  ["path", { d: "M12 19v3" }],
  ["path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }],
  ["rect", { x: "9", y: "2", width: "6", height: "13", rx: "3" }],
]);
export const PencilLine = createLucideIcon("PencilLine", [
  ["path", { d: "M13 21h8" }],
  ["path", { d: "m15 5 4 4" }],
  [
    "path",
    {
      d: "M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z",
    },
  ],
]);
export const TriangleAlert = createLucideIcon("TriangleAlert", [
  ["path", { d: "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" }],
  ["path", { d: "M12 9v4" }],
  ["path", { d: "M12 17h.01" }],
]);
export const CloudCheck = createLucideIcon("CloudCheck", [
  ["path", { d: "m17 15-5.5 5.5L9 18" }],
  ["path", { d: "M5.516 16.07A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 3.501 7.327" }],
]);
// Lucide `arrow-up-right` — the dashboard's «Клиническая уверенность» tiles use it as their open-link
// affordance (Figma component "Icon / arrow-up-right", node 7674:28114, described there as
// "direction, north-east, diagonal").
export const ArrowUpRight = createLucideIcon("ArrowUpRight", [
  ["path", { d: "M7 7h10v10" }],
  ["path", { d: "M7 17 17 7" }],
]);
// Rendered FILLED, matching Lucide's own default for these two transport glyphs.
export const Play = createLucideIcon(
  "Play",
  [
    [
      "path",
      {
        d: "M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z",
      },
    ],
  ],
  { fill: "currentColor" },
);
export const Pause = createLucideIcon(
  "Pause",
  [
    ["rect", { x: "14", y: "3", width: "5", height: "18", rx: "1" }],
    ["rect", { x: "5", y: "3", width: "5", height: "18", rx: "1" }],
  ],
  { fill: "currentColor" },
);
export const Bell = createLucideIcon("Bell", [
  ["path", { d: "M10.268 21a2 2 0 0 0 3.464 0" }],
  ["path", { d: "M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" }],
]);
export const Trash2 = createLucideIcon("Trash2", [
  ["path", { d: "M10 11v6" }],
  ["path", { d: "M14 11v6" }],
  ["path", { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" }],
  ["path", { d: "M3 6h18" }],
  ["path", { d: "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" }],
]);
export const Download = createLucideIcon("Download", [
  ["path", { d: "M12 15V3" }],
  ["path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }],
  ["path", { d: "m7 10 5 5 5-5" }],
]);
// Stand-in for the iOS Safari share-sheet glyph (box + upward arrow) — Lucide has no exact match for
// Apple's icon, this is the closest existing shape; the install instructions carry the real explanation.
export const IosShare = createLucideIcon("IosShare", [
  ["path", { d: "M12 3v12" }],
  ["path", { d: "m17 8-5-5-5 5" }],
  ["path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }],
]);
